import { Constants } from "src/billinglatest/constants";
import type { PixiPurchaseOrder } from "./finalizePurchase";

/**
 * Redirect-based payment providers whose completion returns to the app via a
 * gateway return path, so the purchase must be resumed after the round-trip.
 */
export type PixiPurchaseProvider = "paypal" | "direct_debit";

/**
 * Everything needed to resume a redirect-based purchase after the gateway
 * round-trip: which provider, the gateway token, the org it belongs to (guards
 * against resuming into the wrong org), and the order to finalize.
 */
export interface PixiPurchaseResume {
  provider: PixiPurchaseProvider;
  token: string;
  orgId: string;
  order: PixiPurchaseOrder;
}

/** sessionStorage key for the pending redirect-purchase resume marker. */
export const PURCHASE_RESUME_KEY = "pixi-purchase-resume";

/** sessionStorage key for the one-shot post-resume notice message. */
export const PURCHASE_NOTICE_KEY = "pixi-purchase-notice";

/** sessionStorage key for the one-shot post-redirect purchase-success flag. */
export const PURCHASE_SUCCESS_KEY = "pixi-purchase-success";

/**
 * Gateway return paths keyed by provider. Reuses the billing-latest gateway
 * paths so redirect-based Pixi purchases land on the same completion routes:
 * PayPal → PaypalReturnPath, Direct Debit (GoCardless) → GocardlessReturnPath.
 */
export const PIXI_RETURN_PATHS: Record<PixiPurchaseProvider, string> = {
  paypal: Constants.GatewayPaths.PaypalReturnPath,
  direct_debit: Constants.GatewayPaths.GocardlessReturnPath,
};

/**
 * Inverse of {@link PIXI_RETURN_PATHS}, keyed by the `:provider` segment of the
 * gateway return path. PayPal returns to `/complete/paypal` and Direct Debit
 * (GoCardless) to `/complete/bank`, so the route segment ("bank") differs from
 * the marker's provider name ("direct_debit"). The return-path handler maps the
 * segment back to the {@link PixiPurchaseProvider} before matching it against
 * the saved marker. Unknown segments map to `undefined` (no match → fail shut).
 */
export const PIXI_RETURN_PATH_PROVIDERS: Record<string, PixiPurchaseProvider> =
  {
    paypal: "paypal",
    bank: "direct_debit",
  };

/** Persist the resume marker for a pending redirect-based purchase. */
export const savePurchaseResume = (resume: PixiPurchaseResume): void => {
  try {
    window.sessionStorage.setItem(PURCHASE_RESUME_KEY, JSON.stringify(resume));
  } catch {
    // sessionStorage may be unavailable (private mode, quota); ignore.
  }
};

/**
 * Read the resume marker. Defensive: missing or corrupt JSON → null so a bad
 * marker never crashes the return-path handler.
 */
export const readPurchaseResume = (): PixiPurchaseResume | null => {
  try {
    const raw = window.sessionStorage.getItem(PURCHASE_RESUME_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PixiPurchaseResume;
  } catch {
    return null;
  }
};

/** Remove the resume marker (call once the purchase has been resumed). */
export const clearPurchaseResume = (): void => {
  try {
    window.sessionStorage.removeItem(PURCHASE_RESUME_KEY);
  } catch {
    // ignore
  }
};

/** Persist a one-shot notice to surface after the purchase resumes. */
export const saveResumeNotice = (message: string): void => {
  try {
    window.sessionStorage.setItem(PURCHASE_NOTICE_KEY, message);
  } catch {
    // ignore
  }
};

/**
 * Read and remove the one-shot notice (consume-once). Returns null when there
 * is nothing to show.
 */
export const consumeResumeNotice = (): string | null => {
  try {
    const message = window.sessionStorage.getItem(PURCHASE_NOTICE_KEY);
    if (message === null) return null;
    window.sessionStorage.removeItem(PURCHASE_NOTICE_KEY);
    return message;
  } catch {
    return null;
  }
};

/** Whether a one-shot notice is pending, without consuming it. */
export const hasResumeNotice = (): boolean => {
  try {
    return window.sessionStorage.getItem(PURCHASE_NOTICE_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * Flag a redirect-based purchase (PayPal / Direct Debit) as successfully
 * completed, so the billing page can auto-reopen the wizard on the Complete
 * (success) screen — mirroring the inline card/invoice flow, which flips to
 * that screen in place. Written only by the Pixi return handler.
 */
export const savePurchaseSuccess = (): void => {
  try {
    window.sessionStorage.setItem(PURCHASE_SUCCESS_KEY, "1");
  } catch {
    // ignore
  }
};

/**
 * Read and remove the success flag (consume-once). Returns true exactly once
 * after a successful redirect purchase, so the wizard shows the Complete screen
 * a single time rather than stickily re-showing it on every reopen.
 */
export const consumePurchaseSuccess = (): boolean => {
  try {
    const present =
      window.sessionStorage.getItem(PURCHASE_SUCCESS_KEY) !== null;
    if (present) window.sessionStorage.removeItem(PURCHASE_SUCCESS_KEY);
    return present;
  } catch {
    return false;
  }
};

/** Whether a success flag is pending, without consuming it. */
export const hasPurchaseSuccess = (): boolean => {
  try {
    return window.sessionStorage.getItem(PURCHASE_SUCCESS_KEY) !== null;
  } catch {
    return false;
  }
};
